import type { StripePaymentRequest, TmPaymentRequest } from '@common/entities';
import { get, post } from 'config/api/config';

import { extractErrorCode, PaymentError } from './payment';

export type {
  StripePaymentRequest,
  TmPayerDetailsRequest,
  TmPaymentRequest,
  TmStudentDetailsRequest,
} from '@common/entities';

export interface InternationalPaymentSettings {
  readonly enableInternationalLeasingJourney?: boolean;
  /** API versions have returned both snake_case and camelCase keys. */
  readonly is_international?: boolean;
  readonly isInternational?: boolean;
  readonly enable?: boolean;
  readonly is_card_enable?: boolean;
  readonly isCardEnable?: boolean;
  readonly transfermate?: {
    readonly enable?: boolean;
    readonly is_card_enable?: boolean;
    readonly isCardEnable?: boolean;
    readonly isShowStudentNumberField?: boolean;
  };
}

export interface InternationalAvailablePaymentType {
  readonly id: number;
  readonly name: string;
  readonly maxPaymentAmount?: number;
  readonly fee?: number;
  readonly requireCvv?: boolean;
  readonly requireBusinessAccount?: boolean;
  /** Current viewer API key. */
  readonly chargesDueAutoPaymentEnable?: boolean;
  /** Kept for compatibility with older gateway responses. */
  readonly charges_due_auto_payment_enable?: boolean;
  readonly reason?: string;
  readonly isTemporary?: boolean;
  readonly cardTypes?: readonly {
    readonly cardTypeId: number;
    readonly name: string;
    readonly reason?: string;
    readonly isTemporary?: boolean;
  }[];
}

export interface InternationalAvailablePaymentTypesResponse {
  readonly available: readonly InternationalAvailablePaymentType[];
  readonly unavailable: readonly InternationalAvailablePaymentType[];
}

export type InternationalAvailablePaymentTypesResult =
  | InternationalAvailablePaymentTypesResponse
  | readonly InternationalAvailablePaymentType[];

export interface StripeCheckoutSuccessResponse {
  readonly paymentId: number | string;
  readonly checkoutSessionId: string;
  readonly checkoutSessionUrl: string;
  readonly successUrl: string;
  readonly cancelUrl: string;
}

export interface StripeCheckoutFailureResponse {
  readonly code: number | string;
  readonly message: string;
}

export type StripePaymentResponse = StripeCheckoutSuccessResponse | StripeCheckoutFailureResponse;

export interface PaymentOperationRequest {
  applicantId?: number;
  applicationId?: number;
  arPaymentId: string;
  localArPaymentId: string;
  localStripePaymentKey: string;
  status: string;
}

export interface PaymentOperationResponse {
  success?: boolean;
  message?: string;
  ar_payment_id?: number | string;
  arPaymentId?: number | string;
  status?: string | null;
  data?: {
    status?: string | null;
  };
}

export interface TransferMatePaymentCompletionRequest {
  transactionId: string;
  status: string;
}

export interface TmPaymentResponse {
  success?: boolean;
  message?: string;
  code?: number;
  field?: string;
  paymentId: number | string;
  paymentKey?: string;
  redirectUrl?: string;
}

export interface FxConversionRequest {
  countryCode: string;
  amount: string;
  indiaSourceOfFund?: string;
  indiaDeclarationAmt?: string;
  payerDocument?: string;
}

export interface FxConversionTax {
  readonly name?: string;
  readonly amount?: string | number;
  readonly percentage?: string | number;
}

export interface FxConversionRate {
  readonly payment_method_id?: string | number;
  readonly paymentMethodId?: string | number;
  readonly type?: string;
  readonly logo?: string;
  readonly source_amount?: string | number;
  readonly sourceAmount?: string | number;
  readonly source_currency?: string;
  readonly sourceCurrency?: string;
  readonly converted_amount?: string | number;
  readonly convertedAmount?: string | number;
  readonly converted_currency?: string;
  readonly convertedCurrency?: string;
  readonly fx_rate?: string | number;
  readonly fxRate?: string | number;
  readonly taxes?: readonly FxConversionTax[];
}

export interface FxConversionResponse {
  readonly success: boolean;
  readonly message?: string;
  readonly data?: {
    readonly rates?: readonly FxConversionRate[];
  };
}

export const getPaymentSettings = async () => {
  try {
    const response = await get<InternationalPaymentSettings>('viewer/settings');
    return response;
  } catch (error: any) {
    const enhancedError: PaymentError = error as PaymentError;
    const errorCode = extractErrorCode(error?.response?.data);
    if (errorCode !== undefined) {
      enhancedError.errorCode = errorCode;
    }
    throw enhancedError;
  }
};

export const getAvailablePaymentTypes = async (amount: number) => {
  if (amount === null || amount === undefined) {
    throw new Error('Please provide valid amount.');
  }

  try {
    const response = await get<InternationalAvailablePaymentTypesResponse>(
      `viewer/available-payment-types?amount=${encodeURIComponent(amount)}`,
    );
    return response;
  } catch (error: any) {
    const enhancedError: PaymentError = error as PaymentError;
    const errorCode = extractErrorCode(error?.response?.data);
    if (errorCode !== undefined) {
      enhancedError.errorCode = errorCode;
    }
    throw enhancedError;
  }
};

export const stripePayment = async (data: StripePaymentRequest) => {
  try {
    return await post<StripePaymentResponse>('viewer/stripe-payment', data);
  } catch (error: any) {
    const enhancedError: PaymentError = error as PaymentError;
    const errorCode = extractErrorCode(error?.response?.data);
    if (errorCode !== undefined) {
      enhancedError.errorCode = errorCode;
    }
    throw enhancedError;
  }
};

export const tmPayment = async (data: TmPaymentRequest) => {
  try {
    return await post<TmPaymentResponse>('viewer/tm-payment', data);
  } catch (error: any) {
    const enhancedError: PaymentError = error as PaymentError;
    const errorCode = extractErrorCode(error?.response?.data);
    if (errorCode !== undefined) {
      enhancedError.errorCode = errorCode;
    }
    throw enhancedError;
  }
};

export const completeTransferMatePayment = async (data: TransferMatePaymentCompletionRequest) => {
  try {
    return await post<{ success: boolean; paymentId: string }>('viewer/tm-payment/complete', data);
  } catch (error: any) {
    const enhancedError: PaymentError = error as PaymentError;
    const errorCode = extractErrorCode(error?.response?.data);
    if (errorCode !== undefined) {
      enhancedError.errorCode = errorCode;
    }
    throw enhancedError;
  }
};

export const fxConversion = async (data: FxConversionRequest) => {
  try {
    return await post<FxConversionResponse>('viewer/fx-conversion', data);
  } catch (error: any) {
    const enhancedError: PaymentError = error as PaymentError;
    const errorCode = extractErrorCode(error?.response?.data);
    if (errorCode !== undefined) {
      enhancedError.errorCode = errorCode;
    }
    throw enhancedError;
  }
};

export const paymentOperation = async (data: PaymentOperationRequest) => {
  try {
    return await post<PaymentOperationResponse>('viewer/payment-operation', data);
  } catch (error: any) {
    const enhancedError: PaymentError = error as PaymentError;
    const errorCode = extractErrorCode(error?.response?.data);
    if (errorCode !== undefined) {
      enhancedError.errorCode = errorCode;
    }
    throw enhancedError;
  }
};
